import '@components/Model/model.css'

import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  ControlOutlined,
  PartitionOutlined,
  RightOutlined,
  SettingFilled,
} from '@ant-design/icons'
import * as animationData from '@assets/Animation/cube_loader.json'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { AutoComplete, Col, Row, Select, Switch, Tabs } from 'antd'
import { addDays } from 'date-fns'
import moment from 'moment/moment'
import React, { useMemo, useState } from 'react'
import Lottie from 'react-lottie'
import { useSetup } from '@api/useSetup'

const { TabPane } = Tabs
const { Option } = Select
const statusDictionary = {
  done: 'Was Successful',
  Success: 'Was Successful',
  Failure: 'Failed',
  Running: 'Currently Running',
  not_started: 'Currently Running',
}

export function Configuration({
  sheetKey,
  status,
  startRunDateTime,
  lastRunDateTime,
  markets,
  options,
  setOptions,
  selectedMarkets,
  setSelectedMarkets,
  showModal,
  onSplitSwitchClick,
  onSmartSupplySwitchClick,
  onDemandModelSwitchClick,
  runNewModel,
  solverState,
  setMarketSearchValue,
  marketSearchValue,
  isSupply,
  start,
  setStart,
  duration,
  setDuration,
  metadata,
  runModelWithSplit,
  runSmartSupplyAfter,
  runDemandModel,
}) {
  const [errorSpan, setErrorSpan] = useState({ start: null, duration: null })
  const { scopes } = useSecurityContext()
  const canRunModel = scopes.model.download && scopes.scopes.includes('o.w')
  const handleRunNewModel = () => {
    const errors = {}
    if (!start) errors.start = 'Please select a start date'
    if (!duration) errors.duration = 'Please select a duration'
    if (Object.keys(errors).length > 0) {
      setErrorSpan(errors)
    } else {
      runNewModel()
    }
  }
  return (
    <Col span={6} className={`flex-1 ${!isSupply && 'mt-3'}`} style={{ display: 'flex', flexDirection: 'column' }}>
      <span className='section-title'>{isSupply ? 'MODEL STATUS' : 'RUN NEW OPTIMIZATION'}</span>
      <div className='run-new-model-container mt-2' style={{ flex: 1 }}>
        <LastModelRunResults
          solverState={solverState}
          status={status}
          startRunDateTime={startRunDateTime}
          lastRunDateTime={lastRunDateTime}
        />
        {!isSupply && (
          <>
            <MarketSelectTabs
              markets={markets}
              selectedMarkets={selectedMarkets}
              setSelectedMarkets={setSelectedMarkets}
              setOptions={setOptions}
              options={options}
              solverState={solverState}
              marketSearchValue={marketSearchValue}
              setMarketSearchValue={setMarketSearchValue}
              canRunModel={canRunModel}
            />
            <TimeSelector
              duration={duration}
              setDuration={setDuration}
              start={start}
              setStart={setStart}
              metadata={metadata}
              errorSpan={errorSpan}
              setErrorSpan={setErrorSpan}
            />
            <RunWithSplitSwitch
              runModelWithSplit={runModelWithSplit}
              onClick={onSplitSwitchClick}
              canRunModel={canRunModel}
            />
            <RunWithSmartSupply
              runSmartSupplyAfter={runSmartSupplyAfter}
              onClick={onSmartSupplySwitchClick}
              canRunModel={canRunModel}
            />
            <RunDemandModel
              runDemandModel={runDemandModel}
              onClick={onDemandModelSwitchClick}
              canRunModel={canRunModel}
            />
            <MoreModelOptions onClick={() => showModal()} canRunModel={canRunModel} />
            {canRunModel && (
              <div className='p-3'>
                <GraviButton
                  success
                  style={{ borderRadius: 0 }}
                  buttonText='Run New Model'
                  onClick={handleRunNewModel}
                  className='run-new-model'
                />
              </div>
            )}
          </>
        )}
      </div>
    </Col>
  )
}

export function LastModelRunResults({ solverState, status, lastRunDateTime, isSupply }) {
  const data = getStatusValues(status, solverState, isSupply)

  return (
    <div
      className={`last-model-run-container p-3 flex-1 ${isSupply ? 'supply' : ''}`}
      style={{ maxHeight: isSupply ? '100%' : 300 }}
    >
      {data.Icon}
      <span className='last-model-run-label mt-4'>
        {data.Label}
        <span className={`pl-1 ${data.Class}`}>{statusDictionary[status]}</span>
      </span>
      <span className='last-model-run-date pt-2 mb-4'>{`@ ${lastRunDateTime}`}</span>
    </div>
  )
}

const getStatusValues = (status, solverState, isSupply) => {
  const data = {}

  switch (status) {
    case 'Running':
      data.Label = isSupply ? 'Smart Supply' : `${solverState.running_market_names?.length} Market(s) are`
      data.Class = 'running'
      data.Icon = (
        <span className='mt-3'>
          <LoadingAnimation />
        </span>
      )
      break
    case 'Failure':
      data.Label = 'Last Model Run'
      data.Class = 'failed'
      data.Icon = <CloseCircleFilled className='last-model-run-icon failed' />
      break
    default:
      data.Label = 'Last Model Run'
      data.Class = 'success'
      data.Icon = <CheckCircleFilled className='last-model-run-icon success' />
  }

  return data
}

function LoadingAnimation() {
  return (
    <Lottie
      options={{
        loop: true,
        autoplay: true,
        animationData: animationData.default,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid slice',
        },
      }}
      height={90}
      width={100}
      isStopped={false}
      isPaused={false}
    />
  )
}

function MarketSelectTabs({
  storeCount,
  markets,
  setOptions,
  options,
  selectedMarkets,
  setSelectedMarkets,
  solverState,
  marketSearchValue,
  setMarketSearchValue,
  canRunModel,
}) {
  if (!canRunModel) return null
  return (
    <div className='market-select-container p-3'>
      Select the Applicable Markets
      <Tabs defaultActiveKey='1'>
        <TabPane
          tab={
            <span>
              <ControlOutlined className='mr-3' />
              Specific Markets
            </span>
          }
          key='1'
        >
          <SpecificMarketsTabContent
            markets={markets}
            solverState={solverState}
            options={options}
            setOptions={setOptions}
            selectedMarkets={selectedMarkets}
            setSelectedMarkets={setSelectedMarkets}
            marketSearchValue={marketSearchValue}
            setMarketSearchValue={setMarketSearchValue}
          />
        </TabPane>
        <TabPane
          disabled={solverState.running_market_names.length > 0}
          tab={
            <span>
              <PartitionOutlined className='mr-3' />
              All Markets
            </span>
          }
          key='2'
        >
          <AllMarketsTabContent storeCount={storeCount} />
        </TabPane>
      </Tabs>
    </div>
  )
}

function AllMarketsTabContent({ storeCount }) {
  return (
    <div className='all-markets-container pl-3 pt-2 pb-2'>
      <span className='section-title'>All Markets</span>
      <div className='store-count'>{storeCount} Sites</div>
    </div>
  )
}

function SpecificMarketsTabContent({
  markets,
  selectedMarkets,
  setSelectedMarkets,
  setOptions,
  options,
  solverState,
  marketSearchValue,
  setMarketSearchValue,
}) {
  const onSearch = (value) => {
    const v = value.toLowerCase()
    setOptions(!value ? markets : markets.filter((m) => m.market.toLowerCase().includes(v)))
  }

  const onSelect = (value) => {
    const copy = [...selectedMarkets]
    const selectedMatch = copy.filter((x) => x.market.toLowerCase() === value.toLowerCase())

    if (selectedMatch.length > 0) return

    const match = markets.filter((x) => x.market.toLowerCase() === value.toLowerCase())[0]
    copy.push(match)
    setSelectedMarkets(copy)
  }
  const filteredOptions = options.filter((op) => !solverState.running_market_names.includes(op.market))
  const renderTitle = (title) => <span>{title}</span>

  const renderItem = (index, title) => {
    return {
      value: title,
      label: (
        <div
          key={index + title}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div className='bold-text'>{title}</div>
        </div>
      ),
      index,
    }
  }

  const test = [
    {
      label: renderTitle('Markets'),
      options: filteredOptions && filteredOptions.map((m, i) => renderItem(i, m.market)),
    },
  ]

  const removeMarket = (market) => {
    const copy = [...selectedMarkets]
    copy.splice(
      copy.findIndex((c) => c.market === market.market),
      1
    )
    setSelectedMarkets(copy)
  }

  return (
    <div>
      <AutoComplete
        options={test}
        style={{ width: '100%' }}
        onSelect={onSelect}
        onSearch={onSearch}
        onChange={(value) => setMarketSearchValue(value)}
        placeholder='Search for a Market'
        value={marketSearchValue}
      />
      <div className='mt-3'>
        {selectedMarkets.map((s) => {
          const match = markets.filter((m) => s.market.toLowerCase() === m.market.toLowerCase())[0]
          return (
            <div className='all-markets-container selected pl-3 pt-2 pb-2 mt-2' key={match.market}>
              <Row>
                <Col span={6}>
                  <div className='section-title'>{match.market}</div>
                </Col>
                <Col span={6}>
                  <div className='store-count mt-1'>{match.store_count} Sites</div>
                </Col>
                <Col span={6} align='right'>
                  <div onClick={() => removeMarket(match)} className='remove-market mt-1 pr-3'>
                    Remove
                  </div>
                </Col>
              </Row>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TimeSelector({ start, setStart, duration, setDuration, metadata, setErrorSpan, errorSpan }) {
  const { scopes } = useSecurityContext()
  const canRunModel = scopes.model.download && scopes.scopes.includes('o.w')

  const durationList = useMemo(() => {
    if (metadata) {
      return metadata.duration_options.map((d, i) => {
        return { value: d, label: d === '1' ? `${d} Day` : `${d} Days` }
      })
    }
  }, [metadata])
  const startList = useMemo(() => {
    if (metadata) {
      return metadata.start_options.map((s, i) => {
        return { value: i + 1, label: s }
      })
    }
  }, [metadata])

  if (!canRunModel) return null
  return (
    <div className='p-3'>
      <Horizontal>
        <Col flex={1}>
          <Texto>Start</Texto>
          <Select
            onFocus={() => {
              setErrorSpan({ ...errorSpan, start: '' })
            }}
            onChange={(value) => setStart(value)}
            value={start}
            placeholder='Select start'
            showArrow
            className='start-duration-dropdowns start-duration-selected'
          >
            {startList?.map((d, i) => (
              <Option value={d.value} key={`${i}-start`}>
                {d.label}
              </Option>
            ))}
          </Select>
          {!!errorSpan.start && <Texto style={{ color: 'var(--theme-error)' }}>{errorSpan.start}</Texto>}
        </Col>
        <Col flex={1}>
          <Texto>Duration</Texto>
          <Select
            onFocus={() => {
              setErrorSpan({ ...errorSpan, duration: '' })
            }}
            onChange={(value) => setDuration(value)}
            value={duration}
            placeholder='Select duration'
            showArrow
            className='start-duration-dropdowns start-duration-selected'
          >
            {durationList?.map((d, i) => (
              <Option value={d.value} key={`${i}-duration`}>
                {d.label}
              </Option>
            ))}
          </Select>
          {!!errorSpan.duration && <Texto style={{ color: 'var(--theme-error)' }}>{errorSpan.duration}</Texto>}
        </Col>
      </Horizontal>
    </div>
  )
}

function RunWithSplitSwitch({ onClick, canRunModel, runModelWithSplit }) {
  if (!canRunModel) return null
  return (
    <div className='model-run-with-storage-container p-3'>
      <Row>
        <Col span={12}>Run Model With Split</Col>
        <Col span={12} align='right'>
          <Switch
            checked={runModelWithSplit}
            onClick={onClick}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </Col>
      </Row>
    </div>
  )
}

function RunWithSmartSupply({ onClick, canRunModel, runSmartSupplyAfter }) {
  if (!canRunModel) return null
  return (
    <div className='model-run-with-storage-container p-3'>
      <Row>
        <Col span={12}>Smart Supply After Model</Col>
        <Col span={12} align='right'>
          <Switch
            checked={runSmartSupplyAfter}
            onClick={onClick}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </Col>
      </Row>
    </div>
  )
}

function RunDemandModel({ onClick, canRunModel, runDemandModel }) {
  const { data } = useSetup()
  const demand_model_available = data?.demand_model_available

  if (!demand_model_available) return null
  if (!canRunModel) return null
  return (
    <div className='model-run-with-storage-container p-3'>
      <Row>
        <Col span={12}>Demand Only Model</Col>
        <Col span={12} align='right'>
          <Switch
            checked={runDemandModel}
            onClick={onClick}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </Col>
      </Row>
    </div>
  )
}

function MoreModelOptions({ onClick, canRunModel }) {
  if (!canRunModel) return null
  return (
    <div className='more-model-options-container p-3' onClick={onClick}>
      <Row>
        <Col span={2}>
          <SettingFilled />
        </Col>
        <Col span={20}>More Model Options</Col>
        <Col span={2} align='right'>
          <RightOutlined />
        </Col>
      </Row>
    </div>
  )
}
